/**
 * Redux middleware: pop the enable-notifications flow when a whitelisted
 * fulfilled action dispatches.
 *
 * Matches `action.type` against the keys of `NOTIFICATION_PROMPT_TRIGGERS`;
 * if found, fires `triggerEnableNotificationsFlow(copy)` next tick so the
 * dispatch chain finishes first and the modal opens after the action's own
 * UI side-effects have rendered. Idempotency lives inside the flow
 * component (skips if push permission is granted/denied or recently
 * dismissed).
 */
import type { Middleware } from '@reduxjs/toolkit';

import { triggerEnableNotificationsFlow } from '../components/EnableNotificationsFlow';
import { NOTIFICATION_PROMPT_TRIGGERS } from './notificationsPromptTriggers';

const isFulfilledAction = (action: unknown): action is { type: string } =>
  typeof (action as { type?: unknown })?.type === 'string';

export const notificationsPromptMiddleware: Middleware = () => next => action => {
  const result = next(action);
  if (!isFulfilledAction(action)) return result;
  const copy = NOTIFICATION_PROMPT_TRIGGERS[action.type];
  if (!copy) return result;
  setTimeout(() => triggerEnableNotificationsFlow(copy), 0);
  return result;
};
